import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { StewChat } from "@/components/stew/StewChat";
import { StewHistoryDrawer } from "@/components/stew/StewHistoryDrawer";
import { Button } from "@/components/ui/button";
import { Info, Plus } from "lucide-react";

export default function StewForAgents() {
  const { user } = useAuth();
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState(0);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("stew_conversations")
        .select("id")
        .eq("user_id", user.id)
        .order("updated_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (!cancelled && data?.id) setConversationId(data.id);
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const handleTouched = useCallback(() => {
    setRefreshToken((n) => n + 1);
  }, []);

  return (
    <div className="p-4 md:p-6 space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-serif font-semibold">Stew for Agents</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Internal staff assistant. Same engine, wider corpus.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setConversationId(null)}
          >
            <Plus className="h-4 w-4 mr-1.5" />
            New
          </Button>
          <StewHistoryDrawer
            activeId={conversationId}
            onSelect={setConversationId}
            refreshToken={refreshToken}
          />
        </div>
      </div>

      <div className="flex items-start gap-2 rounded-md border border-amber-300/60 bg-amber-50 dark:bg-amber-950/30 text-amber-900 dark:text-amber-100 px-3 py-2 text-sm">
        <Info className="h-4 w-4 mt-0.5 shrink-0" />
        <p>
          Internal use. Answers may include draft material. Do not quote a
          Draft-badged source to a member as final.
        </p>
      </div>

      <StewChat
        key={conversationId ?? "new"}
        conversationId={conversationId}
        onConversationCreated={(id) => {
          setConversationId(id);
          setRefreshToken((n) => n + 1);
        }}
        onConversationTouched={handleTouched}
        includeUnpublished
        disableDrafting
        surface="internal"
      />
    </div>
  );
}
